import { useCallback, useRef } from 'react';
const OBSERVED_CHANGES = {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled'],
};
function assignRef(ref, value) {
    if (typeof ref === 'function')
        ref(value);
    else if (ref)
        ref.current = value;
}
function isFocusLost() {
    const active = document.activeElement;
    return active === null || active === document.body;
}
/**
 * Un bottone col focus che diventa `disabled` (conferma in corso: «Elimina»,
 * «Rettifica», «Pubblica») o che sparisce lascia il focus sul `body`. Se sotto
 * il dialogo c'è un pannello (Sheet) col proprio FocusScope, Radix lo vede
 * cambiare e gli riporta il focus anche mentre il dialogo lo tiene in
 * `aria-hidden` (il suo MutationObserver non guarda se lo scope è in pausa):
 * «Blocked aria-hidden on an element because its descendant retained focus».
 * Finché il contenuto è aperto e il focus era al suo interno, lo si riporta
 * sul contenuto stesso (Radix gli dà `tabIndex=-1`).
 *
 * Il browser toglie il focus a un bottone appena spento solo al rendering
 * successivo («focus fixup»), dopo l'observer: per un bottone spento col
 * focus si ricontrolla anche dopo il frame. Senza, alla prima modifica del
 * pannello sotto (la risposta del server: «Ritira» che sparisce) Radix
 * portava il focus sul pannello ancora in `aria-hidden` (collaudo del link
 * al cliente).
 */
export function useKeepFocusInContent(forwardedRef) {
    const watchRef = useRef(null);
    const forwardedRefRef = useRef(forwardedRef);
    forwardedRefRef.current = forwardedRef;
    return useCallback((node) => {
        assignRef(forwardedRefRef.current, node);
        if (!node) {
            // Radix riaggancia il ref a ogni render (null, poi lo stesso
            // elemento): staccare subito l'observer perderebbe la mutazione del
            // `disabled` appena scritta nello stesso commit.
            const watched = watchRef.current;
            queueMicrotask(() => {
                if (watched && !watched.node.isConnected) {
                    watched.stop();
                    if (watchRef.current === watched)
                        watchRef.current = null;
                }
            });
            return;
        }
        if (watchRef.current?.node === node)
            return;
        watchRef.current?.stop();
        watchRef.current = { node, stop: watchFocus(node) };
    }, []);
}
/** Il focus è su un elemento spento dentro il contenuto (fixup in arrivo). */
function isFocusOnDisabledInside(node) {
    const active = document.activeElement;
    return (active instanceof HTMLElement &&
        node.contains(active) &&
        active.matches(':disabled'));
}
/**
 * Esegue `callback` dopo il prossimo rendering, quando il browser ha già
 * applicato il focus fixup. Restituisce la funzione che lo annulla.
 */
function runAfterNextRendering(callback) {
    let timeout;
    if (typeof requestAnimationFrame !== 'function') {
        timeout = setTimeout(callback, FALLBACK_FRAME_MS);
        return () => clearTimeout(timeout);
    }
    const frame = requestAnimationFrame(() => {
        timeout = setTimeout(callback, 0);
    });
    return () => {
        cancelAnimationFrame(frame);
        clearTimeout(timeout);
    };
}
/** Durata di un frame quando `requestAnimationFrame` non c'è. */
const FALLBACK_FRAME_MS = 16;
/** Osserva il contenuto e restituisce la funzione che smette di farlo. */
function watchFocus(node) {
    let focusWasInside = node.contains(document.activeElement);
    let cancelFixupCheck = null;
    const trackFocus = (event) => {
        focusWasInside =
            event.target instanceof Node && node.contains(event.target);
    };
    const keepFocusInside = () => {
        if (!focusWasInside || !node.isConnected)
            return;
        if (node.getAttribute('data-state') === 'closed')
            return;
        if (isFocusLost())
            node.focus({ preventScroll: true });
    };
    const observer = new MutationObserver(() => {
        keepFocusInside();
        if (!isFocusOnDisabledInside(node))
            return;
        cancelFixupCheck?.();
        cancelFixupCheck = runAfterNextRendering(() => {
            cancelFixupCheck = null;
            keepFocusInside();
        });
    });
    document.addEventListener('focusin', trackFocus);
    observer.observe(node, OBSERVED_CHANGES);
    return () => {
        document.removeEventListener('focusin', trackFocus);
        observer.disconnect();
        cancelFixupCheck?.();
    };
}
//# sourceMappingURL=use-keep-focus-in-content.js.map